import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  useAdminSelectionMutation,
  useCalculateSolarEstimationCostAdminMutation,
  useChangeSolarEstimationStatusMutation,
  useLazyViewBranchListAdminQuery,
  useLazyViewSolarEstimationUpdateHistoryQuery,
} from "../../../../../redux/api/solar-estimation/solarEstimationAPI";
import ChangeSolarEstimationStatusRequestDTO from "../../../../../shared/oversight-core/dtos/request-dtos/change-solar-estimation-status-request-dto";
import { ISolarEstimationUserSelection } from "../../../../../shared/oversight-core/dtos/request-dtos/solar-estimation-user-selection-request-dto";
import AdminViewBranchListResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/admin-view-branch-list-response-dto";
import CalculateSolarEstimationCostResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/calculate-solar-estimation-cost-response-dto";
import { ViewSolarEstimationUpdateHistoryResponseDTO } from "../../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-update-history-response-dto";
import { EstimationStatus } from "../../../../../shared/oversight-core/enums/estimation-status";
import { ESolarSolutionType } from "../../../../../shared/oversight-core/enums/sola-solution-type";
import { EViewSolarEstimationSolarProductCategory } from "../../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import { ISolarEstimationUpdateHistoryView } from "../../../../../shared/oversight-core/interfaces/solar-estimation-update-history-view";
import AppSelect, {
  Option,
} from "../../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppInput from "../../../../../shared/oversight-core/ui-elements/input/app-input";
import { MtIcon } from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import SpinnerModal from "../../../../../shared/oversight-core/ui-elements/spinner/spinner";
import { formatWithCommas } from "../../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../../shared/oversight-core/utils/helpers";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";
import SolarHistoryCard from "../solar-history-card/solar-history-card";
import styles from "./approve-system-modal.module.scss";

interface IProps extends ModelContainerProps {
  show: boolean;
  estimationId: string;
  user: string;
  selectedStatus: EstimationStatus;
  currency: string;
  isAdminModify?: boolean;
  onShowPurchaseHistory: () => void;
  setIsUpdateCurrentSate: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IFormInput {
  status?: string;
  adminComment?: string;
  branchIdentity: Option | [];
  siteLocation?: string;
  laborCostForDistance?: number | null;
  totalAccessoriesCost: number | null;
  installationCost?: number | null;
}

const defaultFormValues: IFormInput = {
  status: "",
  adminComment: "",
  branchIdentity: [],
  siteLocation: "",
  laborCostForDistance: null,
  totalAccessoriesCost: null,
  installationCost: null,
};

const ApproveSystemModal = (props: IProps) => {
  const {
    show,
    estimationId,
    user,
    selectedStatus,
    currency,
    isAdminModify,
    setIsUpdateCurrentSate,
    ...rest
  } = props;

  const [updateHistory, setUpdateHistory] = useState<
    ISolarEstimationUpdateHistoryView[]
  >([]);
  const [branchList, setBranchList] = useState<Option[]>([]);

  const currentVersion = updateHistory[updateHistory.length - 1];

  const [
    triggerViewSolarEstimationUpdateHistory,
    { isFetching: isFetchingSolarEstimationUpdateHistory },
  ] = useLazyViewSolarEstimationUpdateHistoryQuery();
  const [triggerBranchList, { isFetching: isFetchingBranchList }] =
    useLazyViewBranchListAdminQuery();
  const [
    triggerCalculateSolarEstimationCost,
    { isLoading: isLoadingCalculateSolarEstimationCost },
  ] = useCalculateSolarEstimationCostAdminMutation();
  const [triggerChangeSolarEstimationStatus, { isLoading }] =
    useChangeSolarEstimationStatusMutation();
  const [triggerAdminSelection, { isLoading: isLoadingAdminSelection }] =
    useAdminSelectionMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const currentBranch = watch("branchIdentity") as Option;

  useEffect(() => {
    if (show) {
      triggerBranchList()
        .unwrap()
        .then((res: AdminViewBranchListResponseDTO) => {
          const sortedBranches = res.branchList
            .slice()
            .sort((a, b) => a.name.trim().localeCompare(b.name.trim()));

          setBranchList(
            sortedBranches.map((i) => ({
              value: i.identity,
              label: i.name,
            }))
          );
        })
        .catch(() => {
          setBranchList([]);
        });
    }
  }, [triggerBranchList, show]);

  useEffect(() => {
    if (currentBranch.value && estimationId) {
      triggerCalculateSolarEstimationCost({
        estimationId,
        branchIdentity: currentBranch.value,
      })
        .unwrap()
        .then((res: CalculateSolarEstimationCostResponseDTO) => {
          setValue("siteLocation", res.siteLocation.locationName);
          setValue(
            "laborCostForDistance",
            Number(Helper.roundTo2(res.laborCostForDistance))
          );
          setValue(
            "totalAccessoriesCost",
            Number(Helper.roundTo2(res.totalAccessoriesCost))
          );
          setValue(
            "installationCost",
            Number(Helper.roundTo2(res.totalLaborCost))
          );
        });
    }
  }, [triggerCalculateSolarEstimationCost, estimationId, currentBranch.value]);

  useEffect(() => {
    if (show) {
      triggerViewSolarEstimationUpdateHistory({ estimationId })
        .unwrap()
        .then((res: ViewSolarEstimationUpdateHistoryResponseDTO) => {
          setUpdateHistory(res.history);
        })
        .catch(() => {
          setUpdateHistory([]);
        });
    }
  }, [show]);

  const onSubmit = (data: IFormInput) => {
    const hasChangeProducts = currentVersion?.after.products.some((as) => {
      const match = currentVersion?.before.products.find((a) => a.id === as.id);
      return !match || match.count !== as.count;
    });

    const requestBody: ChangeSolarEstimationStatusRequestDTO = {
      estimationId: estimationId,
      status:
        isAdminModify &&
        hasChangeProducts &&
        selectedStatus !== EstimationStatus.REJECTED
          ? EstimationStatus.APPROVED_WITH_CHANGES
          : selectedStatus,
    };

    if (data.adminComment) {
      requestBody.adminComment = data.adminComment;
    }

    if (data.branchIdentity) {
      requestBody.branchIdentity = (data.branchIdentity as Option).value;
    }

    if (data.totalAccessoriesCost) {
      requestBody.accessoryCost = data.totalAccessoriesCost;
    }

    const userSelections: ISolarEstimationUserSelection[] =
      currentVersion?.after.products.map((us) => ({
        productCategory: us.category,
        productId: String(us.id),
        count: us.count,
        isSelected: true,
      }));

    if (userSelections.length > 0) {
      triggerAdminSelection({
        estimationId: estimationId,
        userSelections,
      })
        .unwrap()
        .then(() => {
          triggerChangeSolarEstimationStatus({ ...requestBody })
            .unwrap()
            .then(() => {
              setIsUpdateCurrentSate((ps) => !ps);
              showSuccessMessage(
                `${
                  selectedStatus === EstimationStatus.REJECTED
                    ? `System Rejected Successfully`
                    : `System Approved Successfully`
                }`
              );
              props.onClose?.();
            })
            .catch(() => {
              showErrorMessage(
                `${
                  selectedStatus === EstimationStatus.REJECTED
                    ? `Failed to Reject the System`
                    : `Failed to Approve the System`
                }`
              );
            });
        });
    }
  };

  const findIcon = (category: EViewSolarEstimationSolarProductCategory) => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        return "google_tv_remote";
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        return "battery_horiz_000";
      case EViewSolarEstimationSolarProductCategory.PANELS:
        return "solar_power";
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        return "propane";
      default:
        return "tablet_mac";
    }
  };

  const findName = (id: string) => {
    return currentVersion?.products.find((i) => i.id === id)?.name ?? "";
  };

  const findAdminSelectedProduct = (
    category: EViewSolarEstimationSolarProductCategory
  ): { icon: MtIcon; name: string; count: number; id: string } => {
    const products =
      currentVersion?.after.products.length >
      currentVersion?.before.products.length
        ? currentVersion.before.products
        : currentVersion.after.products;

    const selectedProducts = products.find((i) => i.category === category);
    return {
      icon: findIcon(category),
      name: selectedProducts ? findName(selectedProducts.id) : "",
      count: selectedProducts ? selectedProducts.count : 0,
      id: selectedProducts ? selectedProducts.id : "",
    };
  };

  const solarCategories = [
    EViewSolarEstimationSolarProductCategory.INVERTERS,
    EViewSolarEstimationSolarProductCategory.BATTERIES,
    EViewSolarEstimationSolarProductCategory.PANELS,
  ];

  const waterCategories = [
    EViewSolarEstimationSolarProductCategory.WATER_HEATERS,
    EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS,
  ];

  return (
    <ModalContainer
      {...rest}
      title={
        selectedStatus === EstimationStatus.REJECTED
          ? `Reject System`
          : `Approve System`
      }
      show={show}
      size="modal-xl"
      confirmButtonText="Confirm"
      cancelButtonText="Cancel"
      onConfirm={handleSubmit(onSubmit)}
      isLoading={isLoading || isLoadingAdminSelection}
      onCancel={() => {
        reset({ ...defaultFormValues });
        props.onCancel?.();
      }}
      onClose={() => {
        reset({ ...defaultFormValues });
        props.onClose?.();
      }}
    >
      <>
        <Row className="bg-background-10 px-1 py-3 rounded-2 mx-0 mt-4">
          <Col>
            <Row className="align-items-center mb-2">
              <Col className="col-auto pe-0 text-label font-weight-500 font-size-20">
                Version&nbsp;{currentVersion?.version}
              </Col>
              <Col className="text-secondary font-weight-400 font-size-10">
                {moment(currentVersion?.before?.lastUpdated).format(
                  "hh:mm a DD/MM/YYYY"
                )}
              </Col>
            </Row>
            <Row className="align-items-center justify-content-between mx-0">
              <Col className="col-auto status-background text-color-6 py-1 px-3 rounded-5 font-weight-400 font-size-12">
                Submitted by&nbsp;{user}
              </Col>
              <Col
                className={`col-auto py-1 px-3 rounded-5 font-weight-400 font-size-12 ${
                  selectedStatus === EstimationStatus.REJECTED
                    ? `text-danger`
                    : `status-approved`
                } status-background`}
              >
                {selectedStatus === EstimationStatus.REJECTED
                  ? `Rejecting`
                  : ` Approving`}
              </Col>
            </Row>
            {currentVersion?.estimationType !==
              ESolarSolutionType.WATER_HEATING_SYSTEM && (
              <>
                <Row className="mt-3">
                  <Col className="text-secondary font-weight-500 font-size-13">
                    Solar Power Equipments
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {currentVersion?.after?.products.length >
                    currentVersion?.before?.products.length
                      ? currentVersion?.after?.products
                          .filter((i) => solarCategories.includes(i.category))
                          .map((i) => {
                            if (!i.id) return null;

                            return (
                              <Row key={i.id} className="gx-3 my-2">
                                <Col>
                                  {findAdminSelectedProduct(i.category).id ? (
                                    <SolarHistoryCard
                                      icon={
                                        findAdminSelectedProduct(i.category)
                                          .icon
                                      }
                                      name={
                                        findAdminSelectedProduct(i.category)
                                          .name
                                      }
                                      count={
                                        findAdminSelectedProduct(i.category)
                                          .count
                                      }
                                      bgColor={
                                        findAdminSelectedProduct(i.category)
                                          .id !== i.id ||
                                        findAdminSelectedProduct(i.category)
                                          .count !== i.count
                                          ? "bg-background-11"
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <div className="text-center text-label">
                                      -
                                    </div>
                                  )}
                                </Col>
                                <Col>
                                  {i.id ? (
                                    <SolarHistoryCard
                                      icon={findIcon(i.category)}
                                      name={findName(i.id)}
                                      count={i.count}
                                      bgColor={
                                        findAdminSelectedProduct(i.category)
                                          .id !== i.id ||
                                        findAdminSelectedProduct(i.category)
                                          .count !== i.count
                                          ? "bg-background-12"
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <div className="text-center text-label">
                                      -
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            );
                          })
                      : currentVersion?.before?.products
                          .filter((i) => solarCategories.includes(i.category))
                          .map((i) => {
                            if (!i.id) return null;

                            return (
                              <Row key={i.id} className="gx-3 my-2">
                                <Col>
                                  {i.id ? (
                                    <SolarHistoryCard
                                      icon={findIcon(i.category)}
                                      name={findName(i.id)}
                                      count={i.count}
                                      bgColor={
                                        findAdminSelectedProduct(i.category)
                                          .id !== i.id ||
                                        findAdminSelectedProduct(i.category)
                                          .count !== i.count
                                          ? "bg-background-12"
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <div className="text-center text-label">
                                      -
                                    </div>
                                  )}
                                </Col>
                                <Col>
                                  {findAdminSelectedProduct(i.category).id ? (
                                    <SolarHistoryCard
                                      icon={
                                        findAdminSelectedProduct(i.category)
                                          .icon
                                      }
                                      name={
                                        findAdminSelectedProduct(i.category)
                                          .name
                                      }
                                      count={
                                        findAdminSelectedProduct(i.category)
                                          .count
                                      }
                                      bgColor={
                                        findAdminSelectedProduct(i.category)
                                          .id !== i.id ||
                                        findAdminSelectedProduct(i.category)
                                          .count !== i.count
                                          ? "bg-background-11"
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <div className="text-center text-label">
                                      -
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            );
                          })}
                  </Col>
                </Row>
              </>
            )}
            {(currentVersion?.estimationType === ESolarSolutionType.BOTH ||
              currentVersion?.estimationType ===
                ESolarSolutionType.WATER_HEATING_SYSTEM) && (
              <>
                <Row className="mt-2">
                  <Col className="text-secondary font-weight-500 font-size-13">
                    Water Heater Equipments
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {currentVersion?.after?.products.length >
                    currentVersion?.before?.products.length
                      ? currentVersion?.after?.products
                          .filter((i) => waterCategories.includes(i.category))
                          .map((i) => {
                            if (!i.id) return null;

                            return (
                              <Row key={i.id} className="gx-3 my-2">
                                <Col>
                                  {findAdminSelectedProduct(i.category).id ? (
                                    <SolarHistoryCard
                                      icon={
                                        findAdminSelectedProduct(i.category)
                                          .icon
                                      }
                                      name={
                                        findAdminSelectedProduct(i.category)
                                          .name
                                      }
                                      count={
                                        findAdminSelectedProduct(i.category)
                                          .count
                                      }
                                      bgColor={
                                        findAdminSelectedProduct(i.category)
                                          .id !== i.id ||
                                        findAdminSelectedProduct(i.category)
                                          .count !== i.count
                                          ? "bg-background-11"
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <div className="text-center text-label">
                                      -
                                    </div>
                                  )}
                                </Col>
                                <Col>
                                  {i.id ? (
                                    <SolarHistoryCard
                                      icon={findIcon(i.category)}
                                      name={findName(i.id)}
                                      count={i.count}
                                      bgColor={
                                        findAdminSelectedProduct(i.category)
                                          .id !== i.id ||
                                        findAdminSelectedProduct(i.category)
                                          .count !== i.count
                                          ? "bg-background-12"
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <div className="text-center text-label">
                                      -
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            );
                          })
                      : currentVersion?.before?.products
                          .filter((i) => waterCategories.includes(i.category))
                          .map((i) => {
                            if (!i.id) return null;

                            return (
                              <Row key={i.id} className="gx-3 my-2">
                                <Col>
                                  {i.id ? (
                                    <SolarHistoryCard
                                      icon={findIcon(i.category)}
                                      name={findName(i.id)}
                                      count={i.count}
                                      bgColor={
                                        findAdminSelectedProduct(i.category)
                                          .id !== i.id ||
                                        findAdminSelectedProduct(i.category)
                                          .count !== i.count
                                          ? "bg-background-12"
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <div className="text-center text-label">
                                      -
                                    </div>
                                  )}
                                </Col>
                                <Col>
                                  {findAdminSelectedProduct(i.category).id ? (
                                    <SolarHistoryCard
                                      icon={
                                        findAdminSelectedProduct(i.category)
                                          .icon
                                      }
                                      name={
                                        findAdminSelectedProduct(i.category)
                                          .name
                                      }
                                      count={
                                        findAdminSelectedProduct(i.category)
                                          .count
                                      }
                                      bgColor={
                                        findAdminSelectedProduct(i.category)
                                          .id !== i.id ||
                                        findAdminSelectedProduct(i.category)
                                          .count !== i.count
                                          ? "bg-background-11"
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <div className="text-center text-label">
                                      -
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            );
                          })}
                  </Col>
                </Row>
              </>
            )}
            <Row className="mt-3">
              <Col>
                {(!!currentVersion?.before?.equipmentCost ||
                  !!currentVersion?.after?.equipmentCost) && (
                  <Row className="row-cols-auto align-items-center justify-content-between my-1">
                    <Col className="text-label font-weight-500 font-size-16">
                      {currency}&nbsp;
                      {formatWithCommas(
                        Number(
                          Helper.roundTo2(currentVersion?.before?.equipmentCost)
                        )
                      )}
                    </Col>
                    <Col className="text-advance-description font-weight-400 font-size-14">
                      Components Cost
                    </Col>
                    <Col className="text-label font-weight-500 font-size-16">
                      {currency}&nbsp;
                      {formatWithCommas(
                        Number(
                          Helper.roundTo2(
                            currentVersion?.after?.equipmentCost
                              ? currentVersion?.after?.equipmentCost
                              : currentVersion?.before?.equipmentCost
                          )
                        )
                      )}
                    </Col>
                  </Row>
                )}
                {(!!currentVersion?.before?.totalLaborCost ||
                  !!currentVersion?.after?.totalLaborCost) &&
                  currentVersion.installationCostRequested && (
                    <Row className="row-cols-auto align-items-center justify-content-between my-1">
                      <Col className="text-label font-weight-500 font-size-16">
                        {currency}&nbsp;
                        {formatWithCommas(
                          Number(
                            Helper.roundTo2(
                              currentVersion?.before?.totalLaborCost
                            )
                          )
                        )}
                      </Col>
                      <Col className="text-advance-description font-weight-400 font-size-14">
                        Approximate Installation Cost
                      </Col>
                      <Col className="text-label font-weight-500 font-size-16">
                        {currency}&nbsp;
                        {formatWithCommas(
                          Number(
                            Helper.roundTo2(
                              currentVersion?.after?.totalLaborCost
                                ? currentVersion?.after?.totalLaborCost
                                : currentVersion?.before?.totalLaborCost
                            )
                          )
                        )}
                      </Col>
                    </Row>
                  )}
                {(!!currentVersion?.before?.totalAccessoriesCost ||
                  !!currentVersion?.after?.totalAccessoriesCost) &&
                  currentVersion.installationCostRequested && (
                    <Row className="row-cols-auto align-items-center justify-content-between my-1">
                      <Col className="text-label font-weight-500 font-size-16">
                        {currency}&nbsp;
                        {formatWithCommas(
                          Number(
                            Helper.roundTo2(
                              currentVersion?.before?.totalAccessoriesCost
                            )
                          )
                        )}
                      </Col>
                      <Col className="text-advance-description font-weight-400 font-size-14">
                        Approximate Accessories Cost
                      </Col>
                      <Col className="text-label font-weight-500 font-size-16">
                        {currency}&nbsp;
                        {formatWithCommas(
                          Number(
                            Helper.roundTo2(
                              currentVersion?.after?.totalAccessoriesCost
                                ? currentVersion?.after?.totalAccessoriesCost
                                : currentVersion?.before?.totalAccessoriesCost
                            )
                          )
                        )}
                      </Col>
                    </Row>
                  )}
                <Row className="row-cols-auto align-items-center justify-content-between my-1">
                  <Col className="text-label font-weight-600 font-size-20">
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(
                        Helper.roundTo2(
                          currentVersion?.installationCostRequested
                            ? currentVersion?.before?.grandTotal
                            : currentVersion?.before.equipmentCost
                        )
                      )
                    )}
                  </Col>
                  <Col className="text-advance-description font-weight-400 font-size-16">
                    {currentVersion?.installationCostRequested
                      ? `Approximate`
                      : ``}
                    &nbsp;Total
                  </Col>
                  <Col className="text-label font-weight-600 font-size-20">
                    {(() => {
                      let adminValue;
                      if (currentVersion?.after?.grandTotal) {
                        adminValue = currentVersion?.installationCostRequested
                          ? currentVersion?.after?.grandTotal
                          : currentVersion?.after.equipmentCost;
                      } else {
                        adminValue = currentVersion?.installationCostRequested
                          ? currentVersion?.before?.grandTotal
                          : currentVersion?.before.equipmentCost;
                      }
                      return (
                        <>
                          {currency}&nbsp;
                          {formatWithCommas(
                            Number(Helper.roundTo2(adminValue))
                          )}
                        </>
                      );
                    })()}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        <div className="mt-4">
          <Row>
            <Col className="text-label font-weight-500 font-size-14">Note</Col>
          </Row>
          <Row>
            <Col>
              <textarea
                {...register("adminComment", {
                  required: false,
                })}
                name="adminComment"
                placeholder="Note"
                className={`${styles.textArea} w-100`}
              />
            </Col>
          </Row>
          {selectedStatus === EstimationStatus.APPROVED &&
            currentVersion?.installationCostRequested && (
              <>
                <AppSelect
                  className="mt-3"
                  placeholder="Select Branch"
                  options={branchList}
                  label="Provider Branch"
                  control={control}
                  name="branchIdentity"
                  errors={errors}
                  register={register("branchIdentity", {
                    required: "Please select a branch",
                  })}
                  borderRadiusStyle="Other"
                  isRequired
                  menuHeight="250px"
                />
                <Row className="mt-3">
                  <Col>
                    <AppInput
                      label="Site Location"
                      placeholder="Site Location"
                      name="siteLocation"
                      errors={Error}
                      isRequired={false}
                      register={register("siteLocation", {
                        required: false,
                      })}
                      borderRadiusStyle="Other"
                      disabled
                    />
                  </Col>
                  <Col>
                    <AppInput
                      label="Distance (km)"
                      placeholder="Distance"
                      name="laborCostForDistance"
                      errors={Error}
                      isRequired={false}
                      register={register("laborCostForDistance", {
                        required: false,
                      })}
                      borderRadiusStyle="Other"
                      disabled
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col>
                    <AppInput
                      label={`Actual Installation Cost (${currency})`}
                      placeholder="Actual Installation Cost"
                      name="installationCost"
                      errors={Error}
                      isRequired={false}
                      register={register("installationCost", {
                        required: false,
                      })}
                      borderRadiusStyle="Other"
                      disabled
                    />
                  </Col>
                  <Col>
                    <AppInput
                      label={`Actual Accessories Cost (${currency})`}
                      placeholder="Actual Accessories Cost"
                      name="totalAccessoriesCost"
                      type="number"
                      errors={errors}
                      isRequired={false}
                      borderRadiusStyle="Other"
                      register={register("totalAccessoriesCost", {
                        required: false,
                      })}
                    />
                  </Col>
                </Row>
              </>
            )}
        </div>
        <SpinnerModal
          show={
            isFetchingSolarEstimationUpdateHistory ||
            isFetchingBranchList ||
            isLoadingCalculateSolarEstimationCost
          }
        />
      </>
    </ModalContainer>
  );
};

export default ApproveSystemModal;
